import { RiEditBoxLine } from "react-icons/ri"
import {
  FiMail, FiPhone, FiCalendar, FiUser,
  FiMapPin, FiBriefcase, FiBook, FiGlobe,
  FiLinkedin, FiGithub, FiInstagram, FiExternalLink, FiAward,
} from "react-icons/fi"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import { formattedDate } from "../../../utils/dateFormatter"
import IconBtn from "../../common/IconBtn"

const DetailItem = ({ label, value, icon: Icon }) => (
  <div className="flex flex-col gap-1">
    <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-richblack-500">
      {Icon && <Icon size={12} />}
      {label}
    </p>
    <p className={`text-sm font-medium ${value ? "text-richblack-5" : "text-richblack-500 italic"}`}>
      {value || `Add ${label}`}
    </p>
  </div>
)

const SocialLink = ({ label, value, icon: Icon }) => {
  if (!value) return (
    <div className="flex flex-col gap-1">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-richblack-500">
        {Icon && <Icon size={12} />}
        {label}
      </p>
      <p className="text-sm italic text-richblack-500">Add {label}</p>
    </div>
  )
  return (
    <div className="flex flex-col gap-1">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-richblack-500">
        {Icon && <Icon size={12} />}
        {label}
      </p>
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-sm font-medium text-yellow-50 underline-offset-2 hover:underline"
      >
        {value.replace(/^https?:\/\//, "")}
        <FiExternalLink size={11} className="shrink-0 text-richblack-400" />
      </a>
    </div>
  )
}

export default function MyProfile() {
  const { user } = useSelector((state) => state.profile)
  const navigate = useNavigate()
  const goSettings = () => navigate("/dashboard/settings")
  const ad = user?.additionalDetails

  const hasEducation   = Array.isArray(ad?.education) && ad.education.length > 0
  const hasProfessional = ad?.jobTitle || ad?.company
  const hasSocial = ad?.linkedin || ad?.github || ad?.instagram || ad?.website

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 md:px-0 pb-10">
      <h1 className="text-2xl font-bold text-richblack-5">My Profile</h1>

      {/* ── Profile card ─────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-2xl border border-richblack-700 bg-richblack-800">
        <div className="h-28 bg-gradient-to-r from-richblack-700 via-richblack-600 to-richblack-700" />
        <div className="relative px-6 pb-5">
          <div className="flex items-end justify-between">
            <div className="-mt-10 flex items-end gap-4">
              <img
                src={
                  user?.image ||
                  `https://api.dicebear.com/5.x/initials/svg?seed=${user?.firstName}`
                }
                alt={user?.firstName}
                className="h-20 w-20 rounded-full border-4 border-richblack-800 object-cover shadow-lg"
              />
              <div className="mb-1">
                <p className="text-lg font-bold text-richblack-5">
                  {user?.firstName} {user?.lastName}
                </p>
                {(ad?.jobTitle || ad?.company) && (
                  <p className="text-xs text-richblack-300">
                    {[ad.jobTitle, ad.company].filter(Boolean).join(" · ")}
                  </p>
                )}
                <p className="text-xs text-richblack-500">{user?.accountType}</p>
              </div>
            </div>
            <button
              onClick={goSettings}
              className="mb-1 flex items-center gap-1.5 rounded-lg border border-richblack-600 bg-richblack-700 px-3 py-1.5 text-xs font-medium text-richblack-200 transition-all hover:border-richblack-500 hover:text-richblack-5"
            >
              <RiEditBoxLine size={13} />
              Edit Profile
            </button>
          </div>
        </div>
      </div>

      {/* ── About ────────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-richblack-700 bg-richblack-800 p-6">
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm font-semibold uppercase tracking-wide text-richblack-400">About</p>
          <button onClick={goSettings} className="text-richblack-400 transition-colors hover:text-yellow-50">
            <RiEditBoxLine size={16} />
          </button>
        </div>
        <p className={`text-sm leading-relaxed ${ad?.about ? "text-richblack-100" : "italic text-richblack-500"}`}>
          {ad?.about || "Write something about yourself…"}
        </p>
      </div>

      {/* ── Personal Details ─────────────────────────────────────── */}
      <div className="rounded-2xl border border-richblack-700 bg-richblack-800 p-6">
        <div className="mb-5 flex items-center justify-between">
          <p className="text-sm font-semibold uppercase tracking-wide text-richblack-400">Personal Details</p>
          <button onClick={goSettings} className="text-richblack-400 transition-colors hover:text-yellow-50">
            <RiEditBoxLine size={16} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <DetailItem label="First Name" value={user?.firstName} icon={FiUser} />
          <DetailItem label="Last Name"  value={user?.lastName}  icon={FiUser} />
          <DetailItem label="Email"      value={user?.email}     icon={FiMail} />
          <DetailItem label="Phone"      value={ad?.contactNumber} icon={FiPhone} />
          <DetailItem label="Gender"     value={ad?.gender}      icon={FiUser} />
          <DetailItem
            label="Date of Birth"
            value={ad?.dateOfBirth ? formattedDate(ad.dateOfBirth) : null}
            icon={FiCalendar}
          />
          {ad?.address && (
            <div className="col-span-full">
              <DetailItem label="Address" value={ad.address} icon={FiMapPin} />
            </div>
          )}
        </div>
      </div>

      {/* ── Education ────────────────────────────────────────────── */}
      {hasEducation && (
        <div className="rounded-2xl border border-richblack-700 bg-richblack-800 p-6">
          <div className="mb-5 flex items-center justify-between">
            <p className="text-sm font-semibold uppercase tracking-wide text-richblack-400">Education</p>
            <button onClick={goSettings} className="text-richblack-400 transition-colors hover:text-yellow-50">
              <RiEditBoxLine size={16} />
            </button>
          </div>

          <div className="flex flex-col gap-5">
            {ad.education.map((edu, i) => (
              <div key={i} className="relative border-l-2 border-richblack-600 pl-5">
                {/* timeline dot */}
                <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-yellow-50 bg-richblack-800" />

                <div className="flex flex-wrap items-baseline gap-x-2">
                  <p className="text-sm font-semibold text-richblack-5">{edu.collegeName}</p>
                  {edu.university && (
                    <p className="text-xs text-richblack-400">{edu.university}</p>
                  )}
                </div>

                {edu.courseName && (
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-richblack-300">
                    <FiBook size={11} /> {edu.courseName}
                  </p>
                )}

                <div className="mt-1.5 flex flex-wrap gap-2">
                  {(edu.startYear || edu.passingYear) && (
                    <span className="flex items-center gap-1 rounded-full bg-richblack-700 px-2.5 py-0.5 text-[11px] text-richblack-300">
                      <FiCalendar size={10} />
                      {edu.startYear && edu.passingYear
                        ? `${edu.startYear} – ${edu.passingYear}`
                        : edu.startYear
                        ? `From ${edu.startYear}`
                        : `Until ${edu.passingYear}`}
                    </span>
                  )}
                  {edu.grade && (
                    <span className="flex items-center gap-1 rounded-full bg-richblack-700 px-2.5 py-0.5 text-[11px] text-richblack-300">
                      <FiAward size={10} /> {edu.grade}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Professional ─────────────────────────────────────────── */}
      {hasProfessional && (
        <div className="rounded-2xl border border-richblack-700 bg-richblack-800 p-6">
          <div className="mb-5 flex items-center justify-between">
            <p className="text-sm font-semibold uppercase tracking-wide text-richblack-400">Professional</p>
            <button onClick={goSettings} className="text-richblack-400 transition-colors hover:text-yellow-50">
              <RiEditBoxLine size={16} />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {ad?.jobTitle && <DetailItem label="Job Title" value={ad.jobTitle} icon={FiBriefcase} />}
            {ad?.company  && <DetailItem label="Company"   value={ad.company}  icon={FiBriefcase} />}
          </div>
        </div>
      )}

      {/* ── Social Links ─────────────────────────────────────────── */}
      {hasSocial && (
        <div className="rounded-2xl border border-richblack-700 bg-richblack-800 p-6">
          <div className="mb-5 flex items-center justify-between">
            <p className="text-sm font-semibold uppercase tracking-wide text-richblack-400">Social Links</p>
            <button onClick={goSettings} className="text-richblack-400 transition-colors hover:text-yellow-50">
              <RiEditBoxLine size={16} />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {ad?.linkedin  && <SocialLink label="LinkedIn"         value={ad.linkedin}  icon={FiLinkedin}  />}
            {ad?.github    && <SocialLink label="GitHub"           value={ad.github}    icon={FiGithub}    />}
            {ad?.instagram && <SocialLink label="Instagram"        value={ad.instagram} icon={FiInstagram} />}
            {ad?.website   && <SocialLink label="Personal Website" value={ad.website}   icon={FiGlobe}     />}
          </div>
        </div>
      )}
    </div>
  )
}
